/**
 * A project's sections (R3; Jason, #9: "design reviews shouldn't be visible because nothing that triggers a design
 * review exists yet"): which pages a job has, read from what the job is and the steps it has, never a fixed list. One
 * rule, read by NSB's project menu and the funnel's links, and sent with the project (`ProjectDto.sections`).
 *
 * Always: Overview, Timeline, Email, Files, the design board, Billing, Process, People, Banners, Settings.
 * Design reviews once a step that delivers a review is on the job or an iteration exists (a delivering step opens
 * its iteration as it is added, so the iteration is the one fact); Decisions whenever a live step of the job is the
 * client's, as their Decisions page lists them (a decision they decide, work they do, a gate they approve, a task of
 * theirs in NSB's step: L-23, its panel linking there); Budget and Client view never on NSB's own job, which has no
 * client.
 */

/** Each section by the path of its page under the project (`overview` is the project's home). */
export const PROJECT_SECTIONS = [
  'overview',
  'timeline',
  'mail',
  'files',
  'board',
  'patterns',
  'reviews',
  'budget',
  'decisions',
  'spent',
  'process',
  'people',
  'banners',
  'client',
  'settings',
] as const;
export type ProjectSection = (typeof PROJECT_SECTIONS)[number];

/** What the rule reads of a job. */
export interface SectionFacts {
  /** NSB's own job: no client. */
  internal: boolean;
  /** An iteration of a design review exists on it (a step that delivers one opens it as it is added). */
  reviews: boolean;
  /** A live step of the job is the client's: theirs to decide, do or approve, a task of theirs among them. */
  decisions: boolean;
  /**
   * A live step of the job carries Pattern mapping, its language open (spec "Pattern Mapping" §4.3); a client's own is
   * read once that step is reached, so for a client the API sets it only then.
   */
  patterns: boolean;
}

export function projectSections(f: SectionFacts): ProjectSection[] {
  return PROJECT_SECTIONS.filter((s) => {
    if (s === 'reviews') return f.reviews;
    if (s === 'decisions') return f.decisions;
    if (s === 'patterns') return f.patterns;
    if (s === 'budget' || s === 'client') return !f.internal;
    return true;
  });
}

/**
 * The address of a section's page under a project (`overview` is its home; `rest` follows the section's path). Links
 * into a project are made through it, so one can only name a page the portal has: a line on the timeline from a page
 * since taken out (the estimate, time) has no link rather than one that lands on the home.
 */
export function projectPage(projectId: string, section: ProjectSection = 'overview', rest = ''): string {
  return `/p/${projectId}${section === 'overview' ? '' : `/${section}`}${rest}`;
}

/**
 * Whether a job has a client side at all (B39; Jason, 2026-10-07: "make access match View as"): a project started from
 * its lead, and a client's job, not NSB's own. One rule, read by everything that lets a client in: the invitations and a
 * password set by hand on People, a client's sign-in to the project, and an admin's View as. A lead has no client page
 * yet; NSB's own job has no client.
 */
export function opensToClients(p: { convertedAt: unknown; processKind: string | null }): boolean {
  return p.convertedAt !== null && p.convertedAt !== undefined && p.processKind !== 'internal';
}

/** Why a job has no client side, in words, or null when it has one. */
export function closedToClientsWhy(p: { convertedAt: unknown; processKind: string | null }): string | null {
  if (opensToClients(p)) return null;
  return p.processKind === 'internal' ? 'NSB’s own job has no client to invite.' : 'A lead has no project page for a client yet: start the project first.';
}
